import { ACCOUNT_PASSWORD_MIN_LENGTH, MAILBOX_PASSWORD_MIN_LENGTH } from "@bafe/config";

/**
 * Password policy.
 *
 * Mailbox passwords are held to a higher bar than Bafe account passwords:
 * they are exposed to the public internet over SMTP and IMAP, where they face
 * continuous credential stuffing.
 */

/**
 * Passwords common enough that an attacker tries them first. This is a
 * last-resort screen; production also checks a breach corpus via
 * `checkCompromised`.
 */
const COMMON_PASSWORDS = new Set([
	"password",
	"password1",
	"password123",
	"passw0rd",
	"123456",
	"12345678",
	"123456789",
	"1234567890",
	"qwerty",
	"qwerty123",
	"letmein",
	"welcome",
	"welcome1",
	"admin",
	"administrator",
	"iloveyou",
	"monkey",
	"dragon",
	"sunshine",
	"princess",
	"football",
	"baseball",
	"abc123",
	"changeme",
	"secret",
	"master",
	"login",
	"starwars",
	"whatever",
	"trustno1",
]);

export interface PasswordCheck {
	ok: boolean;
	/** Plain-language reason, safe to render. */
	message?: string;
}

function baseChecks(password: string, minLength: number, label: string): PasswordCheck {
	if (password.length < minLength) {
		return { ok: false, message: `Use at least ${minLength} characters for your ${label}.` };
	}
	if (password.length > 256) {
		return { ok: false, message: "That password is too long." };
	}

	const normalized = password.toLowerCase();
	if (COMMON_PASSWORDS.has(normalized)) {
		return { ok: false, message: "That password is too common. Choose something harder to guess." };
	}
	if (/^(.)\1+$/.test(password)) {
		return { ok: false, message: "That password repeats a single character." };
	}
	if (/^(?:0123456789|1234567890|abcdefghij|qwertyuiop)/.test(normalized)) {
		return { ok: false, message: "That password is a keyboard sequence. Choose something harder to guess." };
	}

	return { ok: true };
}

export function checkAccountPassword(password: string): PasswordCheck {
	return baseChecks(password, ACCOUNT_PASSWORD_MIN_LENGTH, "password");
}

export function checkMailboxPassword(password: string, emailAddress?: string): PasswordCheck {
	const base = baseChecks(password, MAILBOX_PASSWORD_MIN_LENGTH, "mailbox password");
	if (!base.ok) return base;

	if (emailAddress) {
		const localPart = emailAddress.split("@")[0]?.toLowerCase() ?? "";
		if (localPart.length >= 3 && password.toLowerCase().includes(localPart)) {
			return { ok: false, message: "Do not use the email address inside the password." };
		}
	}

	// Mailbox credentials are attacked directly, so require some variety.
	const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z0-9]/].filter((re) => re.test(password)).length;
	if (classes < 3) {
		return {
			ok: false,
			message: "Mix upper case, lower case, numbers or symbols in your mailbox password.",
		};
	}

	return { ok: true };
}

const GENERATOR_ALPHABET = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const GENERATOR_SYMBOLS = "!@#$%^&*-_=+";

/**
 * Generates a strong mailbox password using the platform CSPRNG.
 * Ambiguous glyphs (l, I, 0, O, 1) are excluded because these get read aloud
 * and retyped into phone mail clients.
 */
export function generateMailboxPassword(length = 20): string {
	const alphabet = GENERATOR_ALPHABET + GENERATOR_SYMBOLS;
	const bytes = new Uint32Array(length);
	crypto.getRandomValues(bytes);

	let password = "";
	for (let i = 0; i < length; i += 1) {
		password += alphabet[bytes[i]! % alphabet.length];
	}

	// Regenerate rather than patch, so the output stays uniformly distributed.
	return checkMailboxPassword(password).ok ? password : generateMailboxPassword(length);
}

/** Generates a strong account password for admin provisioning flows. */
export function generateAccountPassword(length = 16): string {
	const alphabet = GENERATOR_ALPHABET + GENERATOR_SYMBOLS;
	const bytes = new Uint32Array(length);
	crypto.getRandomValues(bytes);

	let password = "";
	for (let i = 0; i < length; i += 1) {
		password += alphabet[bytes[i]! % alphabet.length];
	}

	return checkAccountPassword(password).ok ? password : generateAccountPassword(length);
}
